import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  IconButton,
  SelectChangeEvent,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { SANS_FAMILLE } from '../../constants/filtres';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { AppFilterResetButton } from '../layout/AppFilterGrid';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../reduxStore/Store';
import { setFilters, resetFilters } from '../../reduxStore/StockSlice';
import FamilleAutoC from '../AutoComplete/FamilleAutoC';
import SelectDepot from '../Selects/SelectDepot';
import SelectDepartement from '../Selects/SelectDepartement';
import ArticleAutoC from '../AutoComplete/ArticleAutoC';
import { Article, Departement, FamilleData, GeneriqueData } from '../../models/models';
import { GeneriqueDataType } from '../../constants/enums';

interface StockFiltersProps {
  /** Affiche le filtre Sous seuil sécurité (uniquement pertinent en mode "État du stock") */
  showSousSecurite?: boolean;
  /** Affiche les filtres date début/fin (pertinents en mode "Mouvements") */
  showDateRange?: boolean;
}

const StockFilters: React.FC<StockFiltersProps> = ({
  showSousSecurite = true,
  showDateRange    = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { filters } = useSelector((state: any) => state.StockSlice);
  // Le filtre dept n'a de sens qu'en mode "Tous départements" — en mode dept,
  // le contexte global filtre déjà, dupliquer le sélecteur prête à confusion.
  const isAllMode = useSelector((state: any) => state.AppSlice.isAllMode);

  const [showFilters,      setShowFilters]      = useState(false);
  const [resetKey,         setResetKey]         = useState(0);
  const [selectedArticle,  setSelectedArticle]  = useState<Article | null>(null);

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    dispatch(setFilters({ [name]: value === 'null' ? null : Number(value) as 0 | 1 }));
  };

  const handleFamilleChange = (famille: FamilleData | null) => {
    dispatch(setFilters({ famille_id: famille?.id ?? null }));
  };

  const handleDepotChange = (depot: GeneriqueData | null) => {
    dispatch(setFilters({ depot_id: depot?.id ?? null }));
  };

  const handleDepartementChange = (dept: Departement | null) => {
    dispatch(setFilters({ depot_id: null, filter_dept_id: dept?.id ?? null }));
  };

  const handleArticleChange = (article: Article | null) => {
    setSelectedArticle(article);
    dispatch(setFilters({
      code:        article?.code        ?? null,
      designation: article?.designation ?? null,
    }));
  };

  const handleDateChange = (field: 'date_debut' | 'date_fin') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setFilters({ [field]: e.target.value || null }));
    };

  const handleReset = () => {
    dispatch(resetFilters());
    setSelectedArticle(null);
    setResetKey((prev) => prev + 1);
  };

  return (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton
          onClick={() => setShowFilters((prev) => !prev)}
          sx={{
            transform: showFilters ? 'rotate(0deg)' : 'rotate(180deg)',
            transition: 'transform 0.3s ease',
          }}
        >
          <KeyboardArrowDownIcon />
        </IconButton>
        <span>Filtres</span>
      </Box>

      <Collapse in={showFilters}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))',
            alignItems: 'center',
            gap: 2,
            p: 2,
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: 2,
            mb: 2,
          }}
        >
          {/* Article (code ou désignation) */}
          <Box>
            <ArticleAutoC
              key={`article-${resetKey}`}
              onArticleChange={handleArticleChange}
              selectedArticle={selectedArticle}
            />
          </Box>

          {/* Famille (avec sous-familles récursives côté SP) */}
          <Box>
            <FamilleAutoC
              key={`famille-${resetKey}`}
              slot="main"
              type={GeneriqueDataType.FAMILLE_ARTICLE}
              onFamilleChange={handleFamilleChange}
              label="Famille / Sous-famille"
              disabled={filters.famille_id === SANS_FAMILLE}
            />
          </Box>

          {/* famille_id = 0 → articles sans famille (migration 0133) */}
          <FormControlLabel
            label="Sans famille"
            control={
              <Checkbox
                size="small"
                checked={filters.famille_id === SANS_FAMILLE}
                onChange={(e) => {
                  setResetKey((prev) => prev + 1); // vide l'autocomplete famille
                  dispatch(setFilters({ famille_id: e.target.checked ? SANS_FAMILLE : null }));
                }}
              />
            }
          />

          {/* Département : visible uniquement en mode ALL (filtre page-local
              distinct du contexte global axios). En mode dept, le contexte
              global suffit. */}
          {isAllMode && (
            <Box>
              <SelectDepartement
                key={`departement-${resetKey}`}
                allowAll
                selectedDepartement={
                  filters.filter_dept_id
                    ? ({ id: filters.filter_dept_id, nom: '' } as Departement)
                    : null
                }
                onDepartementChange={handleDepartementChange}
              />
            </Box>
          )}

          {/* Dépôt (remonté quand le dept change pour resetter la sélection) */}
          <Box>
            <SelectDepot
              key={`depot-${resetKey}-${filters.filter_dept_id ?? 'all'}`}
              onDepotChange={handleDepotChange}
            />
          </Box>

          {/* Suivi de stock */}
          <FormControl size="small">
            <InputLabel id="track-stock-label">Suivi stock</InputLabel>
            <Select
              labelId="track-stock-label"
              label="Suivi stock"
              name="track_stock"
              value={filters.track_stock === null ? 'null' : String(filters.track_stock)}
              onChange={handleSelectChange}
            >
              <MenuItem value="null">Tous</MenuItem>
              <MenuItem value="1">Tracké</MenuItem>
              <MenuItem value="0">Non tracké</MenuItem>
            </Select>
          </FormControl>

          {/* Sous seuil de sécurité (état du stock uniquement) */}
          {showSousSecurite && (
            <FormControl size="small">
              <InputLabel id="securite-label">Seuil sécurité</InputLabel>
              <Select
                labelId="securite-label"
                label="Seuil sécurité"
                name="stock_sous_securite"
                value={filters.stock_sous_securite === null ? 'null' : String(filters.stock_sous_securite)}
                onChange={handleSelectChange}
              >
                <MenuItem value="null">Tous</MenuItem>
                <MenuItem value="1">Sous seuil</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Stock nul (état du stock uniquement) — masqué par défaut */}
          {showSousSecurite && (
            <FormControl size="small">
              <InputLabel id="stock-zero-label">Stock nul</InputLabel>
              <Select
                labelId="stock-zero-label"
                label="Stock nul"
                name="masquer_stock_zero"
                value={filters.masquer_stock_zero === null ? 'null' : String(filters.masquer_stock_zero)}
                onChange={handleSelectChange}
              >
                <MenuItem value="1">Masquer</MenuItem>
                <MenuItem value="0">Afficher</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Période (mouvements uniquement) */}
          {showDateRange && (
            <>
              <TextField
                size="small"
                type="date"
                label="Date début"
                value={filters.date_debut ?? ''}
                onChange={handleDateChange('date_debut')}
                InputLabelProps={{ shrink: true }}
               
              />
              <TextField
                size="small"
                type="date"
                label="Date fin"
                value={filters.date_fin ?? ''}
                onChange={handleDateChange('date_fin')}
                InputLabelProps={{ shrink: true }}
               
              />
            </>
          )}

          {/* Visibilité */}
          <FormControl size="small">
            <InputLabel id="visible-label">Visibilité</InputLabel>
            <Select
              labelId="visible-label"
              label="Visibilité"
              name="visible"
              value={filters.visible === null ? 'null' : String(filters.visible)}
              onChange={handleSelectChange}
            >
              <MenuItem value="null">Tous</MenuItem>
              <MenuItem value="1">Visible</MenuItem>
              <MenuItem value="0">Masqué</MenuItem>
            </Select>
          </FormControl>

          <AppFilterResetButton onClick={handleReset} />
        </Box>
      </Collapse>
    </div>
  );
};

export default StockFilters;
